import { useState, FormEvent, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';

import { DropzoneFile } from '@lendica/components';
import { getTruncatedFileName } from '@lendica/utils';
import {
    useFileStatus,
    FileStatus,
    getProcessingMessage,
    useBillProcessing,
} from '../hooks/useBillUploadProgress';

export const MultiUploadDialog = ({
    open,
    onClose,
    files,
}: {
    open: boolean;
    onClose: () => void;
    files: DropzoneFile[];
}) => {
    const navigate = useNavigate();
    const [selectedValue, setSelectedValue] = useState<string>('');
    const { overallStatus, states } = useBillProcessing();

    // Auto-select the best available file when dialog opens or states change
    useEffect(() => {
        if (!open || files.length === 0) {
            setSelectedValue('');
            return;
        }

        // Get current statuses for all files
        const fileStatuses = files.map(file => {
            const state = file.key ? states.get(file.key) : undefined;
            return {
                file,
                state,
                billId: state?.billId,
                status: state?.status,
                fileIdentifier: state?.billId || file.path || file.key || '',
            };
        });

        // Check if current selection is still valid (has a billId)
        const currentSelection = fileStatuses.find(
            ({ fileIdentifier }) => fileIdentifier === selectedValue
        );
        const isCurrentSelectionValid = currentSelection?.billId;

        // Only auto-select if:
        // 1. No current selection, OR
        // 2. Current selection is not valid (no billId), OR
        // 3. A better option (completed) becomes available
        const shouldAutoSelect =
            !selectedValue ||
            !isCurrentSelectionValid ||
            (!currentSelection?.status?.includes('completed') &&
                fileStatuses.some(({ status, billId }) => status === 'completed' && billId));

        if (!shouldAutoSelect) {
            return;
        }

        // Selection priority:
        // 1. First file with 'completed' status and billId
        // 2. First file with any billId (processing success)
        // 3. First file in the list
        const completedFile = fileStatuses.find(
            ({ status, billId }) => status === 'completed' && billId
        );

        const fileWithBillId = fileStatuses.find(({ billId }) => billId);

        const selectedFile = completedFile || fileWithBillId || fileStatuses[0];

        if (selectedFile) {
            setSelectedValue(selectedFile.fileIdentifier);
        }
    }, [open, files, states, selectedValue]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();

        // Find the selected file and ensure it has a billId
        const selectedFile = files.find(file => {
            const state = file.key ? states.get(file.key) : undefined;
            const fileIdentifier = state?.billId || file.path || file.key || '';
            return fileIdentifier === selectedValue;
        });

        const billId = selectedFile?.key ? states.get(selectedFile.key)?.billId : undefined;

        if (billId) {
            navigate(`/paylater/bill/${billId}/confirm`);
            onClose();
        }
    };

    // Check if the selected value corresponds to a file with a valid billId
    const selectedFileHasBillId = useMemo(() => {
        const selectedFile = files.find(file => {
            const state = file.key ? states.get(file.key) : undefined;
            const fileIdentifier = state?.billId || file.path || file.key || '';
            return fileIdentifier === selectedValue;
        });
        return selectedFile?.key ? Boolean(states.get(selectedFile.key)?.billId) : false;
    }, [files, selectedValue, states]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: '8px',
                    backgroundColor: 'color-mix(in srgb, #FCFCFD, #202024 2%)',
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle>Upload Bills</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="body1">
                            {getProcessingMessage(overallStatus)}
                        </Typography>
                        {!selectedFileHasBillId && selectedValue && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Please wait for processing to complete before proceeding.
                            </Typography>
                        )}
                    </DialogContentText>
                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                        <RadioGroup
                            value={selectedValue}
                            onChange={event => {
                                setSelectedValue(event.target.value);
                            }}
                        >
                            {files.map(file => (
                                <FileRadioSelect
                                    key={file.path || file.key}
                                    file={file}
                                    selectedValue={selectedValue}
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ padding: '16px' }}>
                    <Button onClick={onClose} variant="outlined" color="secondary">
                        Save and return
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!selectedValue || !selectedFileHasBillId}
                    >
                        Fund selected bill
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

const FileRadioSelect = ({
    file,
    selectedValue,
}: {
    file: DropzoneFile;
    selectedValue: string;
}) => {
    const { status, error, billId } = useFileStatus(file);
    const isSelected = useMemo(() => {
        return billId === selectedValue;
    }, [billId, selectedValue]);

    return (
        <Box
            key={file.path || file.key}
            sx={theme => ({
                'width': '100%',
                'paddingX': '10px',
                'paddingY': '1px',
                'marginY': '6px',
                'borderRadius': 1,
                'backgroundColor': isSelected ? alpha(theme.palette.primary.main, 0.08) : '#ededf0',
                'border': isSelected
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid transparent`,
                '&:hover': {
                    backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.12)
                        : theme.palette.action.hover,
                    borderColor: isSelected
                        ? theme.palette.primary.main
                        : theme.palette.action.disabled,
                },
            })}
        >
            <FormControlLabel
                value={billId || file.path || file.key}
                control={<Radio />}
                disabled={!billId}
                label={
                    <>
                        {getTruncatedFileName(file.path || file.key || '', 20)}
                        <UploadStatus status={status} error={error} />
                    </>
                }
                sx={{
                    'width': '100%',
                    '& .MuiFormControlLabel-label': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        fontSize: '14px',
                        flex: 1,
                        whiteSpace: 'nowrap',
                    },
                }}
            />
        </Box>
    );
};

const UploadStatus = ({ status, error }: { status: FileStatus; error?: string }) => {
    return (
        <Box
            component="span"
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
            }}
        >
            {status === 'uploading' && (
                <>
                    <Skeleton variant="text" width={64} height={24} />
                    uploading...
                </>
            )}
            {(status === 'upload-success' || status === 'processing') && (
                <>
                    <Skeleton variant="text" width={64} height={24} />
                    processing...
                </>
            )}
            {status === 'processing-success' && (
                <CheckCircleIcon color="success" fontSize="small" />
            )}
            {status === 'processing-error' && (
                <>
                    <ErrorIcon color="error" fontSize="small" />
                    {error}
                </>
            )}
        </Box>
    );
};
